import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { colors } from '../../../../constants/colors';
import { fontFamily, fontSize } from '../../../../constants/text';
import StarRating from '../../../../components/SolvPatternLibrary/StarRating';
import { smallerThan } from '../../../../core/util/styledComponents';
import Button, { ButtonVariations } from '../../../../components/SolvPatternLibrary/Button';
import Write from '../../../../components/SolvPatternLibrary/Icons/Write';
import { LEAVE_REVIEW_MODAL } from '../../Modals/LeaveAReviewTestModal';
import { analyticsTrackEvent } from '../../../../core/analytics';
import { CDP_REVIEWS_TOUCH_WRITE_REVIEW } from '../../../../core/analytics/events';
import { pluralize } from '../../../../core/util/string';
import { REVIEW_CTA_COPY } from '../../../../constants';

const Root = styled.div`
  display: flex;
  padding-bottom: 24px;

  ${smallerThan.sm`
    display: block;
  `}
`;

const LeftRail = styled.div`
  flex: 0 1 240px;
  display: flex;
  flex-direction: column;
`;

const RightRail = styled.div`
  flex: 1;
  min-width: 200px;
  display: flex;
  flex-direction: column;

  ${smallerThan.sm`
    margin-top: 24px;
  `}
`;

const OverallRating = styled.div`
  font-size: ${fontSize.default};
  font-family: ${fontFamily.bold};
  color: ${colors.squid};
  line-height: 24px;
  margin-top: 6px;
`;

const RatingCount = styled.div`
  font-size: ${fontSize.small};
  color: ${colors.blueMonday};
  margin-top: 6px;
`;

const SubRatings = styled.div`
  color: ${colors.squid};
  max-width: 450px;
  margin-bottom: 26px;
`;

const SubRatingContent = styled.div`
  display: flex;
  padding-top: ${({ removetoppadding }) => (removetoppadding ? 0 : '5px')};
`;

const SubRatingCount = styled.div`
  min-width: 64px;
  font-size: ${fontSize.extraSmall};
  color: ${colors.blueMonday};
  padding-left: 10px;
  padding-top: 3px;
`;

const TitleAndStars = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 5px;
  flex: 0 1 350px;

  border-bottom: ${({ removebtmborder }) =>
    removebtmborder ? 0 : `1px solid ${colors.fatherJohnMisty}`};
`;

const SubRatingTitle = styled.span`
  font-size: ${fontSize.mediumSmall};
  display: flex;
  align-items: center;
`;

const Caveat = styled.span`
  font-size: ${fontSize.small};
  color: ${colors.greyLock};
  margin-top: 4px;
`;

const ReviewCta = styled(Button)`
  width: 220px;
  height: 40px;
  font-size: ${fontSize.mediumSmall};
  padding: 0px;
  color: ${colors.squid};
  border: 1px solid ${colors.borderLine};
  margin-right: 12px;
  margin-bottom: 20px;
`;

const StyledWrite = styled(Write)`
  width: 16px;
  margin-right: 8px;
`;

const NoReviews = styled.div`
  font-size: ${fontSize.mediumSmall};
  color: ${colors.squid};
  padding-top: 10px;
  padding-bottom: 20px;
`;

const NoRatingsYet = styled.div`
  font-size: ${fontSize.small};
  color: ${colors.squid};
  padding-top: 10px;
`;

export default class HeaderSummary extends PureComponent {
  static propTypes = {
    locationId: PropTypes.string,
    overallRating: PropTypes.number,
    ratingCount: PropTypes.number,
    waitTimeRating: PropTypes.number,
    waitTimeRatingCount: PropTypes.number,
    qualityOfCareRating: PropTypes.number,
    qualityOfCareRatingCount: PropTypes.number,
    bedsideMannerRating: PropTypes.number,
    bedsideMannerRatingCount: PropTypes.number,
    friendlinessRating: PropTypes.number,
    friendlinessRatingCount: PropTypes.number,
    cleanlinessRating: PropTypes.number,
    cleanlinessRatingCount: PropTypes.number,
    showModal: PropTypes.func,
    isReadOnly: PropTypes.bool,
  };

  handleClickWriteReview = () => {
    const { locationId, showModal } = this.props;

    analyticsTrackEvent(CDP_REVIEWS_TOUCH_WRITE_REVIEW, { locationId });
    showModal(LEAVE_REVIEW_MODAL);
  };

  render = () => {
    const {
      overallRating,
      ratingCount,
      waitTimeRating,
      waitTimeRatingCount,
      qualityOfCareRating,
      qualityOfCareRatingCount,
      bedsideMannerRating,
      bedsideMannerRatingCount,
      friendlinessRating,
      friendlinessRatingCount,
      cleanlinessRating,
      cleanlinessRatingCount,
      isReadOnly,
    } = this.props;

    const subratings = [
      {
        title: 'Wait-Time',
        rating: waitTimeRating,
        ratingCount: waitTimeRatingCount,
        removeTopPadding: true,
      },
      {
        title: 'Quality of Care',
        rating: qualityOfCareRating,
        ratingCount: qualityOfCareRatingCount,
      },
      {
        title: 'Bedside Manner',
        rating: bedsideMannerRating,
        ratingCount: bedsideMannerRatingCount,
      },
      {
        title: 'Staff Friendliness',
        rating: friendlinessRating,
        ratingCount: friendlinessRatingCount,
      },
      {
        title: 'Facility Cleanliness',
        rating: cleanlinessRating,
        ratingCount: cleanlinessRatingCount,
        removeBtmBorder: true,
      },
    ];

    return (
      <Root>
        <LeftRail>
          <StarRating identifier="a" rating={overallRating} size={24} />
          {ratingCount > 0 ? (
            <Fragment>
              <OverallRating>Overall Rating {overallRating}</OverallRating>
              <RatingCount>
                Based on {ratingCount} {pluralize(ratingCount, 'Rating')}
              </RatingCount>
            </Fragment>
          ) : (
            <NoRatingsYet>No Ratings yet</NoRatingsYet>
          )}
        </LeftRail>
        <RightRail>
          {ratingCount > 0 ? (
            <SubRatings>
              {subratings.map((subrating, idx) => {
                const { removeBtmBorder, title, rating, removeTopPadding } = subrating;
                const ratingCount = subrating.ratingCount || 0;

                return (
                  <SubRatingContent key={idx} removetoppadding={removeTopPadding}>
                    <TitleAndStars removebtmborder={removeBtmBorder}>
                      <SubRatingTitle>{title}</SubRatingTitle>
                      <StarRating identifier="a" rating={rating} size={14} />
                    </TitleAndStars>
                    <SubRatingCount>
                      ({ratingCount} {pluralize(ratingCount, 'Rating')})
                    </SubRatingCount>
                  </SubRatingContent>
                );
              })}
            </SubRatings>
          ) : (
            <NoReviews>No reviews for this clinic yet. Be the first!</NoReviews>
          )}
          {!isReadOnly && (
            <ReviewCta variation={ButtonVariations.tertiary} onClick={this.handleClickWriteReview}>
              <StyledWrite /> {REVIEW_CTA_COPY}
            </ReviewCta>
          )}
          <Caveat>
            All reviews are opinions of patients and do not represent the opinion of Solv.
          </Caveat>
        </RightRail>
      </Root>
    );
  };
}
